import './style/app.scss'
import {
    Anotate,
    DrawShape,
    DrawType,
    IPosition
} from './anotate/Anotate'
import { IImageBox, ImageBox } from './anotate/Image'
import {
    ConnectionManager,
    ConnectionType,
    IConnectionMessage,
    IConnectionMessageType
} from './connection/ConnectionManager'
import { BaseAnotate } from './anotate/BaseAnotate'
import { Logger } from './Utils/Logger'
import { UIBuilder } from './UIBuilder'
import { Textbox, ITextBox } from './anotate/TextBox'
import { FileService, IFileUploadServerUrl } from './Utils/FileService'
import { uuid } from './Utils/index'
import { CobrowseSender } from './Cobrowse/CobrowseSender'
import { CobrowseReceiver } from './Cobrowse/CobrowseReceiver'

export interface ICobrowseConfig {
    Enabled: boolean;
    IsSender: boolean;
}

export interface IWhiteboardConfig {
    Container: HTMLDivElement;
    Width: number;
    Height: number;
    Url: string;
    UserId: string;
    SessionId: string;
    BrushColors: Array<string>;
    BrushSize: number;
    Cobrowse: ICobrowseConfig;
    NoOfPositionsPerRequest: number;
    ServerId: number;
    Servers: Array<string>;
    FileUploadServers: Array<IFileUploadServerUrl>;
    WaitTimeToReconnect: number
}

export interface ISession {
    Drawings: Array<DrawShape>;
}
export interface ISessionList {
    [key: string]: ISession;
}

export interface IRemoteAnotations {
    [key: string]: BaseAnotate;
}
export type Drawing = DrawShape | ITextBox | IImageBox;

export interface IUserDrawing {
    UserId: string;
    Shape: Drawing;
}

export class Whiteboard {
    #config: IWhiteboardConfig;
    #mainContainer: HTMLDivElement;
    #appContainer: HTMLDivElement = document.createElement('div');
    #boardContainer: HTMLDivElement;
    #toolboxContainer: HTMLDivElement = document.createElement('div');
    #width: number;
    #height: number;
    #anotate: Anotate;
    #sessionId: string;
    #userId: string;
    #ui: UIBuilder;
    #textbox: Textbox | null = null;
    #imageBox: ImageBox | null = null;
    // base canvas to draw all users' drawings
    #baseAnotate: BaseAnotate;
    // depricated
    // #history: Array<DrawShape> = [];

    // store all remote user's anotation
    #remoteAnotations: IRemoteAnotations = {};
    // div container for all remote user's anotation canvas
    #remoteAnotContainer: HTMLDivElement;
    #redoItems: Array<DrawShape> = [];
    #connection: ConnectionManager | any;
    #log = new Logger('Whiteboard');
    #canvasWidth: number;

    // cache to store drawings for history and redo
    #history: Array<IUserDrawing> = [];
    #redoList: Array<IUserDrawing> = [];

    // color of the brush tool
    #brushColors: Array<string> = [];

    // size of the brush tool
    #brushSize: number;

    // cache for holding update positions to send at once
    #upatePositions: Array<IPosition> = [];

    // no of position items to send
    #noOfPositionsPerRequest = 5;

    #fileUploadService: FileService;

    #serverId: number;

    #cobrowseSender: CobrowseSender | undefined
    #cobrowseReceiver: CobrowseReceiver | undefined

    constructor (config: IWhiteboardConfig) {
        this.#config = config
        const width = config.Width ? config.Width : 500
        const height = config.Height ? config.Height : 500
        const sessionId = config.SessionId ? config.SessionId : this.GetNewUuid()
        const userId = config.UserId ? config.UserId : uuid()
        const serverId = config.ServerId ? config.ServerId : 0

        // whiteboard ui will be rendered in this div element
        this.#mainContainer = config.Container

        // set canvas width excluding toolbox width
        this.#canvasWidth = width - 50

        this.#width = width
        this.#height = height
        this.#brushColors = config.BrushColors
        this.#brushSize = config.BrushSize
        this.#sessionId = sessionId
        this.#userId = userId
        this.#serverId = serverId

        // no of msgs to send per request on draw update
        this.#noOfPositionsPerRequest = config.NoOfPositionsPerRequest ? config.NoOfPositionsPerRequest : 5

        // create new div element and append it to app container
        this.#boardContainer = document.createElement('div')

        // config.container.appendChild(this.boardContainer);
        // create new div element for remote anotations and append to whiteboard container
        this.#remoteAnotContainer = document.createElement('div')

        this.#baseAnotate = new BaseAnotate({
            Container: this.#boardContainer,
            Width: this.#canvasWidth,
            Height: this.#height,
            Type: 'base'
        })

        this.#boardContainer.appendChild(this.#remoteAnotContainer)

        this.initConnection()
        // start connection to server
        this.#fileUploadService = new FileService({
            UserId: this.#userId,
            Servers: config.FileUploadServers
        })
        this.#anotate = new Anotate({
            Container: this.#boardContainer,
            Width: this.#canvasWidth,
            Height: this.#height,
            DefaultTool: DrawType.FreeDraw,
            UserId: this.#userId
        })
        this.#anotate.OnNewShape = this.OnNewShape
        this.#anotate.OnStart = this.OnDrawStart
        this.#anotate.OnUpdate = this.OnDrawUpdate
        this.#anotate.OnFinish = this.OnDrawFinish
        this.#ui = new UIBuilder({
            UserId: this.#userId,
            MainContainer: this.#mainContainer,
            AppContainer: this.#appContainer,
            BoardContainer: this.#boardContainer,
            ToolboxContainer: this.#toolboxContainer,
            Width: this.#width,
            Height: this.#height,
            BrushColors: this.#brushColors,
            BrushSize: this.#brushSize,
            OnUndo: this.OnSelfUndo,
            OnRedo: this.OnSelfRedo,
            OnBrushSizeChange: this.OnBrushSizeChange,
            OnBrushColorChange: this.OnBrushColorChange,
            IsCobrowseEnabled: this.#config.Cobrowse?.Enabled
        })
        this.#ui.OnSelectTool = this.ChangeDrawTool
        this.ChangeBrushColor(this.#brushColors[0])
        this.ChangeBrushSize(5)

        // load cobrowse feature if it is enabled
        this.ConfigureCobrowse(config.Cobrowse)

        // this.OnNewShape({"style":{"brushColor":"#f8920f","brushSize":5},"type":1,"positions":[{"X":235,"Y":145},{"X":250,"Y":136},{"X":272,"Y":125},{"X":294,"Y":119},{"X":328,"Y":114},{"X":364,"Y":109},{"X":429,"Y":109},{"X":455,"Y":109},{"X":493,"Y":112},{"X":502,"Y":116},{"X":512,"Y":123},{"X":517,"Y":128},{"X":518,"Y":132},{"X":520,"Y":145},{"X":525,"Y":165},{"X":527,"Y":179},{"X":527,"Y":192},{"X":527,"Y":200},{"X":522,"Y":214},{"X":514,"Y":223},{"X":507,"Y":229},{"X":498,"Y":230},{"X":487,"Y":232},{"X":473,"Y":234},{"X":460,"Y":234},{"X":444,"Y":234},{"X":433,"Y":234},{"X":422,"Y":234},{"X":413,"Y":233},{"X":404,"Y":230},{"X":393,"Y":224},{"X":386,"Y":221},{"X":380,"Y":216},{"X":379,"Y":215},{"X":378,"Y":214},{"X":377,"Y":216},{"X":370,"Y":222},{"X":360,"Y":232},{"X":355,"Y":241},{"X":347,"Y":251},{"X":342,"Y":258},{"X":334,"Y":268},{"X":329,"Y":277},{"X":323,"Y":284},{"X":318,"Y":291},{"X":315,"Y":297},{"X":307,"Y":307},{"X":306,"Y":311},{"X":301,"Y":320},{"X":299,"Y":326},{"X":298,"Y":331},{"X":296,"Y":339},{"X":296,"Y":345},{"X":296,"Y":353},{"X":296,"Y":359},{"X":296,"Y":367},{"X":296,"Y":373},{"X":298,"Y":377},{"X":302,"Y":382},{"X":312,"Y":392},{"X":319,"Y":395},{"X":330,"Y":397},{"X":347,"Y":401},{"X":360,"Y":403},{"X":379,"Y":403},{"X":398,"Y":403},{"X":419,"Y":403},{"X":438,"Y":403},{"X":454,"Y":400},{"X":468,"Y":396},{"X":480,"Y":388},{"X":485,"Y":385},{"X":491,"Y":380},{"X":498,"Y":375},{"X":503,"Y":370},{"X":506,"Y":366},{"X":507,"Y":359},{"X":507,"Y":353},{"X":507,"Y":349},{"X":507,"Y":343},{"X":507,"Y":337},{"X":507,"Y":331},{"X":505,"Y":325},{"X":501,"Y":320},{"X":488,"Y":308},{"X":474,"Y":297},{"X":454,"Y":289},{"X":435,"Y":284},{"X":416,"Y":282},{"X":402,"Y":280},{"X":389,"Y":280},{"X":373,"Y":280},{"X":358,"Y":285},{"X":341,"Y":294},{"X":327,"Y":302},{"X":310,"Y":315},{"X":295,"Y":326},{"X":283,"Y":336},{"X":270,"Y":346},{"X":261,"Y":356},{"X":251,"Y":364},{"X":242,"Y":367},{"X":237,"Y":372},{"X":231,"Y":375},{"X":224,"Y":380},{"X":223,"Y":382},{"X":217,"Y":383},{"X":216,"Y":384},{"X":215,"Y":385},{"X":160,"Y":93},{"X":179,"Y":84},{"X":204,"Y":77},{"X":238,"Y":70},{"X":267,"Y":68},{"X":306,"Y":60},{"X":346,"Y":57},{"X":428,"Y":57},{"X":485,"Y":57},{"X":509,"Y":63},{"X":531,"Y":74},{"X":543,"Y":80},{"X":553,"Y":88},{"X":567,"Y":96},{"X":579,"Y":109},{"X":589,"Y":118},{"X":595,"Y":130}]});
        // this.OnNewShape({"style":{"brushColor":"#f8920f","brushSize":5},"type":1,"positions":[{"X":145,"Y":145},{"X":136,"Y":136},{"X":125,"Y":125},{"X":119,"Y":119},{"X":114,"Y":114},{"X":109,"Y":109}]});
    }

    initConnection () {
        try {
            this.#connection = new ConnectionManager({
                Servers: this.#config.Servers,
                ServerId: this.#config.ServerId,
                Type: ConnectionType.WEBSOCKET,
                SessionId: this.#sessionId,
                UserId: this.#userId,
                OnReceive: this.OnReceive,
                OnOpen: () => {
                    this.#cobrowseSender?.Init()
                },
                WaitTimeToReconnect: this.#config.WaitTimeToReconnect

            })
            this.#connection.OnDrawingReceived = this.OnDrawingReceived
            this.#connection.ReceivedDrawingStart = this.ReceivedDrawingStart
            this.#connection.ReceivedDrawingUpdate = this.ReceivedDrawingUpdate
            this.#connection.ReceivedDrawingFinish = this.ReceivedDrawingFinish
            this.#connection.ReceiveDrawing = this.ReceiveDrawing
            this.#connection.UserJoined = this.OnUserJoined
            this.#connection.UserLeft = this.OnUserLeft
            this.#connection.Undo = this.OnRemoteUndo
            this.#connection.Redo = this.OnRemoteRedo
            this.#connection.OnSessionData = (shapes: Array<DrawShape>) => {
                this.OnSessionDataReceived(shapes)
            }
            this.#connection.OnCobrowseMessageReceived = (message: IConnectionMessage) => {
                this.#cobrowseReceiver?.OnMessage(message)
            }
            this.#connection.OnResize = (width:number, height:number) => {
                this.changeSize(width, height)
            }
        } catch (ex) {
            console.error('Error while initializing connection', ex)
        }
    }

    ConfigureCobrowse = (config: ICobrowseConfig) => {
        if (config?.Enabled) {
            if (config?.IsSender) {
                this.#mainContainer.classList.add('rr-block')
                this.#mainContainer.classList.add('rr-ignore')

                this.#cobrowseSender = new CobrowseSender({
                    UserId: this.#userId,
                    SessionId: this.#sessionId,
                    OnSendMessage: (message: IConnectionMessage) => {
                        this.#connection.SendMessage(message)
                    },
                    OnResize: (width:number, height:number) => {
                        this.changeSize(width, height)
                    }
                })
            } else {
                // this user is cobrowse receiver

                // create new div element to show sender's view
                const cobrowseContainer = document.createElement('div')
                cobrowseContainer.classList.add('cobrowse-container')
                this.#appContainer.appendChild(cobrowseContainer)

                // make app container's possition absolute
                this.#appContainer.style.position = 'absolute'
                this.#cobrowseReceiver = new CobrowseReceiver({
                    UserId: this.#userId,
                    SessionId: this.#sessionId,
                    Container: cobrowseContainer
                })
            }
            this.#mainContainer.style.pointerEvents = 'none'
            this.#boardContainer.style.pointerEvents = 'none'
            this.#boardContainer.style.background = 'none'
        }
    }

    OnSessionDataReceived (list: Array<DrawShape>) {
        try {
            for (let i = 0; i < list.length; i++) {
                this.OnNewShape(list[i])
            }
        } catch (e) {
            console.error(e)
        }
    }

    ChangeDrawTool = (type: DrawType) => {
        this.#anotate.Disable()
        this.#baseAnotate.Display(true)
        this.#anotate.Clear()
        if (type === DrawType.Textbox) {
            this.#textbox = new Textbox({
                BoardContainer: this.#boardContainer
            })
            this.#textbox.OnTextboxSubmit = this.OnTextboxSubmit
        } else if (type === DrawType.Image) {
            this.#imageBox = new ImageBox({
                BoardContainer: this.#boardContainer,
                FileUploadService: this.#fileUploadService,
                SessionId: this.#sessionId
            })
            this.#imageBox.OnImageSubmit = this.OnImageSubmit
        } else if (type === DrawType.Eraser) {
            this.#baseAnotate.Display(false)
            this.#anotate.Enable()
            this.#history.forEach((drawing, i) => {
                this.#anotate.Draw(drawing.Shape)
            })
            this.#anotate.SelectAnnotationType(type)
        } else {
            this.#anotate.Enable()
            this.#anotate.SelectAnnotationType(type)
        }
    };

    OnTextboxSubmit = (textbox: ITextBox) => {
        this.#baseAnotate.DrawText(textbox)
        this.SendDrawing(textbox)
        this.#history.push({
            UserId: this.#userId,
            Shape: textbox
        })
    };

    OnImageSubmit = (imagebox: IImageBox) => {
        this.#baseAnotate.DrawImage(imagebox)
        this.SendDrawing(imagebox)
        this.#history.push({
            UserId: this.#userId,
            Shape: imagebox
        })
    };

    OnNewShape = (shape: DrawShape) => {
        this.#history.push({
            UserId: this.#userId,
            Shape: shape
        })
        this.#baseAnotate.DrawShape(shape)
        if (this.#anotate.selectedTool.tempObj.Type !== DrawType.Eraser) {
            this.#anotate.Clear()
        }
    };

    Undo = (userId: string) => {
        try {
            for (let i = this.#history.length - 1; i >= 0; i--) {
                if (this.#history[i].UserId === userId) {
                    const item = this.#history.splice(i, 1)[0]
                    this.#redoList.push(item)
                    this.Refresh()
                    return
                }
            }
        } catch (ex) {
            console.log('annotation.undo', ex)
        }
    };

    Redo = (userId: string) => {
        try {
            for (let i = this.#redoList.length - 1; i >= 0; i++) {
                if (this.#redoList[i].UserId === userId) {
                    const item = this.#redoList.splice(i, 1)[0]
                    this.#history.push(item)
                    this.Refresh()

                    return
                }
            }
        } catch (ex) { }
    };

    Refresh () {
        this.#baseAnotate.Clear()
        for (const i in this.#history) {
            if (this.#history[i].Shape.Type === DrawType.Textbox) {
                this.#baseAnotate.DrawText(<ITextBox> this.#history[i].Shape)
            } else if (this.#history[i].Shape.Type === DrawType.Image) {
                this.#baseAnotate.DrawImage(<IImageBox> this.#history[i].Shape)
            }
            this.#baseAnotate.DrawShape(<DrawShape> this.#history[i].Shape)
        }
    }

    OnReceive (message: any) { }
    IncreaseBrushSize () {
        try {
            if (this.#anotate.style.brushSize < 20) {
                return ++this.#anotate.style.brushSize
            }
        } catch (ex) {
            console.log('this.changeBrushSize', ex)
        }
    }

    DecreaseBrushSize () {
        try {
            if (this.#anotate.style.brushSize > 1) {
                return --this.#anotate.style.brushSize
            }
        } catch (ex) {
            console.log('this.decreaseBrushSize', ex)
        }
    }

    GetNewUuid (): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0
            const v = c === 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
        })
    }

    OnDrawingReceived = (message: IConnectionMessage) => {
        if (message.UserId === this.#userId) {
            return
        }
        this.#baseAnotate.Clear()
        this.#baseAnotate.DrawShape(message.Data)

        this.#history.push({
            UserId: message.UserId,
            Shape: message.Data
        })
    };

    OnUserJoined = (userId: string) => {
        this.AddNewUserAnotation(userId)
        this.#cobrowseSender?.Init()
    };

    OnUserLeft = (userId: string) => {
        this.RemoveUserAnotation(userId)
    };

    private AddNewUserAnotation (userId: string) {
        let remoteAnot = this.#remoteAnotations[userId]
        if (!remoteAnot) {
            remoteAnot = this.#remoteAnotations[userId] = new BaseAnotate({
                Container: this.#remoteAnotContainer,
                Width: this.#canvasWidth,
                Height: this.#height,
                Type: 'remote-user'
            })
        }
    }

    private RemoveUserAnotation (userId: string) {
        const anot = this.#remoteAnotations[userId]
        if (anot) {
            anot.Dispose()
        }
    }

    OnDrawStart = (shape: DrawShape) => {
        this.#connection.SendDrawingStart(shape)
    };

    OnDrawUpdate = (pos: IPosition) => {
        if (this.#upatePositions.length < this.#noOfPositionsPerRequest - 1) {
            this.#upatePositions.push(pos)
        } else {
            this.#upatePositions.push(pos)
            this.#connection.SendDrawingUpdate(this.#upatePositions)
            this.#upatePositions = []
        }
    };

    OnDrawFinish = (shape: DrawShape) => {
        shape.UserId = this.#userId
        if (this.#upatePositions.length > 0) {
            this.#connection.SendDrawingUpdate(this.#upatePositions)
            this.#upatePositions = []
        }
        this.#connection.SendDrawingFinish(shape)
    };

    ReceivedDrawingStart = (message: IConnectionMessage) => {
        // if (message.UserId === this.userId) return;
        this.#log.Debug('received draw start: ')
        // this._baseAnotate.StartDrawing(message.Data);
        this.#remoteAnotations[message.UserId].StartDrawing(message.Data)
    };

    ReceivedDrawingUpdate = (message: IConnectionMessage) => {
        // if (message.UserId === this.userId) return;
        this.#log.Debug('received draw update: ')
        // this._baseAnotate.UpdatePositions(message.Data);
        for (let i = 0; i < message.Data.length; i++) {
            this.#remoteAnotations[message.UserId].UpdatePositions(message.Data[i])
        }
    };

    ReceivedDrawingFinish = (message: IConnectionMessage) => {
        // if (message.UserId === this.userId) return;
        this.#log.Debug('received draw finish: ')
        const shape: DrawShape = <DrawShape>message.Data

        if (shape.Type === DrawType.Image || shape.Type === DrawType.Textbox) {
            this.#baseAnotate.DrawShape(shape)
            this.#history.push({
                UserId: message.UserId,
                Shape: shape
            })
            return
        }
        // this._baseAnotate.FinishDrawing(message.Data);
        const remoteAnot = this.#remoteAnotations[message.UserId]
        remoteAnot.FinishDrawing(message.Data)
        this.#baseAnotate.DrawShape(remoteAnot.GetShape())
        remoteAnot.Clear()
        this.#history.push({
            UserId: message.UserId,
            Shape: remoteAnot.GetShape()
        })
    };

    SendDrawing (draw: DrawShape | ITextBox | IImageBox) {
        this.#connection.SendDrawingFinish(draw)
    }

    ReceiveDrawing = (userId: string, drawing: Drawing) => {
        if (drawing.Type === DrawType.Image) {
            drawing = <IImageBox>drawing
            this.#baseAnotate.DrawImage(drawing)
        } else if (drawing.Type === DrawType.Textbox) {
            drawing = <ITextBox>drawing
            this.#baseAnotate.DrawText(drawing)
        }
        this.#history.push({
            UserId: userId,
            Shape: drawing
        })
        //  else {
        //   this._baseAnotate.DrawShape(<DrawShape>drawing);
        // }
    };

    OnSelfUndo = () => {
        this.Undo(this.#userId)
        this.#connection.SendMessage({
            Type: IConnectionMessageType.UNDO,
            UserId: this.#userId,
            Data: null,
            SessionId: this.#sessionId
        })
    };

    OnRemoteUndo = (userId: string) => {
        this.Undo(userId)
    };

    OnSelfRedo = () => {
        this.Redo(this.#userId)
        this.#connection.SendMessage({
            Type: IConnectionMessageType.REDO,
            UserId: this.#userId,
            Data: null,
            SessionId: this.#sessionId
        })
    };

    OnRemoteRedo = (userId: string) => {
        this.Redo(userId)
    };

    private OnBrushColorChange = (color: string): void => {
        this.ChangeBrushColor(color)
    };

    ChangeBrushColor = (color: string) => {
        this.#ui.ChangeColor(color)
        this.#anotate.ChangeBrushColor(color)
    };

    private OnBrushSizeChange = (size: number): void => {
        this.ChangeBrushSize(size)
    };

    ChangeBrushSize = (size: number) => {
        this.#ui.ChangeBrushSize(size)
        this.#anotate.ChangeBrushSize(size)
    };

    dispose = () => {
        this.#remoteAnotations = {}
    }

    changeSize = (width:number, height:number) => {
        this.#height = height
        this.#width = width
        for (const i in this.#remoteAnotations) {
            this.#remoteAnotations[i].changeSize(width, height)
        }
        this.#anotate.changeSize(width, height)
        this.#appContainer.style.width = width + 'px'
        this.#appContainer.style.height = height + 'px'
        this.#baseAnotate.changeSize(width, height)
        this.#cobrowseReceiver?.resize(width, height)
    }
}
